import React from "react";

import { FlexJustify } from "common/fragments/Flex";
import Loading from "common/fragments/Loading";
import NonIdealState from "common/fragments/NonIdealState";
import { HTML } from "common/fragments/TextContent";
import { BlockVariant, BlockView } from "common/views/block/BlockView.view";
import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import MutationButton from "common/views/button/MutationButton";
import { CardVariant, CardView } from "common/views/card/Card.view";
import { DropdownSelectVariant } from "common/views/dropdownSelect/DropdownSelect.view";
import { OptionDropdownSelect } from "common/views/dropdownSelect/OptionDropdownSelect";
import HeadingView, { HeadingVariant } from "common/views/heading/Heading.view";
import { Query } from "core/api/typedQuery";
import DateLabel from "core/components/label/DateLabel";
import { IPanelProps, Panel } from "core/decorators/Panel";
import { isEmpty } from "core/util/ArrayUtils";
import { validateList } from "core/util/ParamUtil";
import StringUtil from "core/util/StringUtil";
import { iDef, iDefL, nDef } from "core/util/TypeUtils";
import { MutationType } from "typegraph";
import { IOrganizationFlaggedTopicParams, Organization } from "./entities/OrganizationFlaggedTopics";
import { Topic, TopicDelete } from "./entities/Topic";
import TopicFlag from "./entities/TopicFlag";
import { FlaggedTopicListConfig } from "./FlaggedTopicListConfig";
import labelProvider from './FlaggedTopicList.labels';
import HtmlDefault from "common/fragments/HtmlDefault";

const { Labels } = labelProvider;
const PARAM_ORG_FLAGGED_SORT = "org.flagged.sort";
const PARAM_ORG_FLAGGED_PAGE_SIZE = "org.flagged.pageSize";

const sortOptions = [
	{value: "-createdOn", label: <Labels.PostDate />},
	{value: "name", label: <Labels.PostTitle />}
];

const OrgFlaggedQuery = Query.queryOne<Organization, IOrganizationFlaggedTopicParams>(Organization);

@Panel({id: "organization-flagged-topic-list", configClass: FlaggedTopicListConfig, pageParams: true,labelProvider})
export class FlaggedTopicListPanel extends React.Component<IPanelProps<FlaggedTopicListConfig>> {

	public render() {
		const { config, pageParams } = this.props;

		const { guid } = pageParams;

		if (nDef(guid)) {
			return (
				<NonIdealState
					title={<Labels.OrganizationEmpty/>}
					description={<Labels.DescriptionEmpty />}
				/>
			);
		}

		const defaultPageSize = iDef(config.pageSize) ? config.pageSize : 10;
		const pageSize = StringUtil.toInteger(pageParams[PARAM_ORG_FLAGGED_PAGE_SIZE], defaultPageSize);

		const params = {
			guid,
			pageSize,
			sort: validateList(['createdOn, name'], pageParams[PARAM_ORG_FLAGGED_SORT], '-createdOn')
		};

		return [
			<BlockView key="refine" variant={BlockVariant.REFINE} viewKey="refine">
				<OptionDropdownSelect
					options={sortOptions}
					onChange={this.onChangeParam(PARAM_ORG_FLAGGED_SORT)}
					value={pageParams[PARAM_ORG_FLAGGED_SORT]}
					variant={DropdownSelectVariant.MINIMAL}
					viewKey="refine-sort"
				>
					<Labels.Sort/>
				</OptionDropdownSelect>
			</BlockView>,
			<div key="data">
				<OrgFlaggedQuery params={params}>
					{({data, loading, refetch}) => {
						if (loading) {
							return <Loading key="loading" />;
						}

						if (!data) {
							return (
								<NonIdealState key="notfound" title="Invalid Organization" description="Unable to locate selected organization." />
							);
						}

						if (isEmpty(data.discussionList)) {
							return (
								<NonIdealState key="empty" title="No Flagged Posts" description="Posts by users in your organization will appear here for your review if they are marked as Innappropriate." />
							);
						}

						return (
							<React.Fragment>
								{data.discussionList.map((topic) => this.renderTopic(topic, refetch))}
								{
									data.discussionList.length >= pageSize && (
										<BlockView variant={BlockVariant.PAGING} viewKey="paging">
											<ButtonView
												onClick={() => this.onChangeParam(PARAM_ORG_FLAGGED_PAGE_SIZE)(pageSize + defaultPageSize)}
												variant={ButtonVariant.PRIMARY}
												viewKey="paging.loadmore"
											>
												<Labels.LoadMore />
											</ButtonView>
										</BlockView>
									)
								}
							</React.Fragment>
						);
					}}
				</OrgFlaggedQuery>
			</div>
		];
	}

	private renderTopic = (topic: Topic, refetch: () => void) => {
		return (
			<CardView variant={CardVariant.SURFACE} viewKey="card">
				<BlockView variant={BlockVariant.CARD_HEADER} viewKey="card-header">
					<FlexJustify>
						<div>
							<Labels.FlaggedTimes args={{flags:topic.stat.flags}} />
						</div>
						<div>
							{
								topic.createdOn && (
									<React.Fragment>
										<Labels.CreatedOn />
										{' '}
										<DateLabel value={topic.createdOn} />
									</React.Fragment>
								)
							}
						</div>
					</FlexJustify>
				</BlockView>
				<BlockView variant={BlockVariant.CARD_CONTENT} viewKey="card-content">
					<HeadingView viewKey="title" children={topic.name} />
					<HeadingView variant={HeadingVariant.H6} viewKey="category">
						<Labels.CategoryName args={{categoryName: topic.category.name}} />
					</HeadingView>
					<HtmlDefault
						html={topic.description}
					/>
				</BlockView>
				<BlockView variant={BlockVariant.CARD_ACTIONS} viewKey="card-actions">
					<MutationButton
						clazz={TopicFlag}
						params={{id: topic.id}}
						method={MutationType.DELETE}
						variant={ButtonVariant.CARD_ACTION}
						onSuccess={() => refetch()}
						viewKey="button-approve"
					>
						<Labels.MarkAsSafe />
					</MutationButton>
					<MutationButton
						clazz={TopicDelete}
						params={{id: topic.id}}
						method={MutationType.DELETE}
						variant={ButtonVariant.CARD_ACTION}
						onSuccess={() => refetch()}
						viewKey="button-delete"
					>
						<Labels.Delete/>
					</MutationButton>
				</BlockView>
			</CardView>
		);
	}

	private onChangeParam = (name: string) => (value: any) => {
		if (iDefL(value)) {
			this.props.actions.pushPageParam(name, value);
		} else {
			this.props.actions.removePageParam(name);
		}
	}
}



// WEBPACK FOOTER //
// ./src/panels/organizationAdmin/FlaggedTopicList.panel.tsx